import { observer } from "mobx-react";
import React from "react";
import { generatePath, RouteComponentProps, withRouter } from "react-router";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import Page from "components/Layout/Page";
import SortingControl from "components/SortingControl";
import FilteringControl from "components/FilteringControl";
import Placeholder from "components/Placeholder";
import NavBar from "../NavBar";
import QuizCard from "./QuizCard";
import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  QuizCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import ROUTES from "navigation/routes";
import { computed } from "mobx";
import QuestionnaireQuizStore from "stores/QuestionnaireQuizStore";
import { TableSortType } from "stores/templates/TableStore";
import InfiniteScroll from "react-infinite-scroll-component";
import { SNACKBAR_TYPES } from "components/Snackbar";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Quiz extends React.Component<Props> {
  @computed get quizzes() {
    return QuestionnaireQuizStore.items;
  }

  @computed get quizListIsEmpty() {
    return this.quizzes.length === 0;
  }

  componentDidMount() {
    QuestionnaireQuizStore.fetch();
  }

  componentWillUnmount() {
    QuestionnaireQuizStore.resetPage();
  }

  fetchMoreQuizzes = async () => {
    const { setMessage } = this.props;

    try {
      await QuestionnaireQuizStore.fetchMoreQuizzes();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    QuestionnaireQuizStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      QuestionnaireQuizStore.fetch();
  };

  onSearchClear = () => {
    QuestionnaireQuizStore.clearSearch();
    QuestionnaireQuizStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    console.log(value);
    QuestionnaireQuizStore.applyOrder(value);
    QuestionnaireQuizStore.resetPage();
    QuestionnaireQuizStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      QuestionnaireQuizStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    QuestionnaireQuizStore.resetPage();
    QuestionnaireQuizStore.fetch();
  };

  onFilterClear = () => {
    QuestionnaireQuizStore.clearFilters();
    QuestionnaireQuizStore.fetch();
  };

  onAddQuiz = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUIZ_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (quiz: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUIZ_DETAILS, {
      quizId: quiz.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (quiz: any) => () => {
    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUIZ_DETAILS, {
      quizId: quiz.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onArchive = (quiz: any) => () => {
    console.log("here start");
  };

  onCopy = (quiz: any) => () => {
    console.log("here start");
  };

  onPublish = (quiz: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = quiz;

    try {
      await QuestionnaireQuizStore.changeQuizStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Quiz"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddQuiz}
              >
                Add Quiz
              </Button>
            </PageControl>
          </>
        }
        isLoading={QuestionnaireQuizStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={QuestionnaireQuizStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={QuestionnaireQuizStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={QuestionnaireQuizStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(QuestionnaireQuizStore.isQuizPublishing ||
          QuestionnaireQuizStore.isQuizCopying) && <OverflowLoader />}
        {!this.quizListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.quizzes.length}
            hasMore={QuestionnaireQuizStore.hasMoreInfo}
            next={this.fetchMoreQuizzes}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.quizzes.map(quiz => (
              <QuizCardWrapper key={quiz.id}>
                <QuizCard
                  title={quiz.title}
                  publishStatus={quiz.publishStatus}
                  onDetailsClick={this.onDetailsClick(quiz)}
                  onArchive={this.onArchive(quiz)}
                  onEdit={this.onEdit(quiz)}
                  onCopy={this.onCopy(quiz)}
                  onPublish={this.onPublish(quiz)}
                />
              </QuizCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              QuestionnaireQuizStore.search
                ? "No matches found. Please try another search."
                : "No quizzes added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Quiz)));
